import { useMemo, useState } from 'react';
import { InAppContentDefinition } from 'modules/graphql/types';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { constructFieldErrors, FieldNames } from 'modules/templateEditor/common';

import {
  rokuDeviceStyle,
  RokuNotificationContainer,
  RokuImage,
  RokuButton,
  RokuBody,
  RokuHeader,
  RokuButtonsContainer,
} from './styles';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { formatHexForState, formatRokuHexForUI } from '../utils';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useInAppPreviewContext } from '../context/InAppPreviewContext';

export const RokuPreview = () => {
  const [isValidMedia, setIsValidMedia] = useState<boolean>(false);
  const { liveTemplate } = useEditableFieldsContext();
  const { ottPayload } = liveTemplate.mediumData as InAppContentDefinition;
  const { messageRokuBody, messageRokuHeader } = useInAppPreviewContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();

  const { hasError: hasRokuImageUrlError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ROKU_FHD_IMAGE,
  });

  const header = messageRokuHeader || ottPayload?.dialogHeader;
  const body = messageRokuBody || ottPayload?.dialogText;

  const cssFormattedHexColor = useMemo(
    () =>
      formatHexForState(
        formatRokuHexForUI(
          ottPayload?.backgroundColor || SETTINGS_DISPLAY_CONTENT.roku.backgroundColor.defaultColor,
        ),
      ),
    [ottPayload?.backgroundColor],
  );

  const hasButtons = !!(ottPayload?.buttons?.length && ottPayload.buttons.length > 0);

  // Show the preview if any field besides backgroundColor or __typename is populated
  const showPreview =
    ottPayload &&
    Object.entries(ottPayload).some(([key, val]) => {
      if (key === 'backgroundColor') {
        return false;
      }
      if (key === '__typename') {
        return false;
      }
      if (key === 'buttons') {
        return !!(val?.length && val.length > 0);
      }
      return !!val;
    });

  return (
    <div data-test="template-editor-in-app-preview" style={rokuDeviceStyle}>
      {showPreview && (
        <RokuNotificationContainer backgroundColor={cssFormattedHexColor} hasButtons={hasButtons}>
          {ottPayload?.fhdImage && !hasRokuImageUrlError && (
            <RokuImage
              data-test={'roku-image'}
              src={ottPayload.fhdImage}
              isValid={isValidMedia}
              onLoad={() => setIsValidMedia(true)}
              onError={() => setIsValidMedia(false)}
            />
          )}
          {header && (
            <RokuHeader data-test="roku-header" color="#FFFFFF">
              {header}
            </RokuHeader>
          )}
          {body && (
            <RokuBody data-test="roku-body" color="#FFFFFF">
              {body}
            </RokuBody>
          )}
          {!!(ottPayload?.buttons?.length && ottPayload.buttons.length > 0) && (
            <RokuButtonsContainer>
              {ottPayload.buttons.map((button, i) => (
                <RokuButton data-test={`roku-button-${i}`} key={button.buttonText} props={button}>
                  {button.buttonText}
                </RokuButton>
              ))}
            </RokuButtonsContainer>
          )}
        </RokuNotificationContainer>
      )}
    </div>
  );
};
